<template>
    <aside class="w-80 shrink-0 flex-col border-r border-default bg-default">
        <div class="flex items-center justify-between border-b border-default px-3 py-3 lg:px-5">
            <LogoComponent />
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-2">
            <UNavigationMenu
                v-model="openGroups"
                orientation="vertical"
                :items="menuItems"
                :ui="navUi"
            />
        </div>

        <SwitchManager class="mt-4 px-3 lg:px-5" />

        <div class="mt-auto px-2 py-4">
            <div class="flex w-full items-center gap-8 lg:justify-between">
                <a href="https://www.imagine.si/" class="max-h-20 w-full" target="_blank">
                    <img alt="Imagine" src="@/assets/images/imagine-logo.png">
                </a>
                <a href="https://it-team.global/" class="max-h-20 w-full" target="_blank">
                    <img alt="IT TEAM d.o.o." src="@/assets/images/it-team-logo.png">
                </a>
            </div>
        </div>
    </aside>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue'

    import LogoComponent from '@/components/LogoComponent.vue'
    import SwitchManager from '@/layouts/components/SwitchManager.vue'
    import { useRoute } from 'vue-router'

    import { navItems } from '@/navigation'
    import { useAppStore } from '@/stores/app.ts'
    import type { NavLink } from '@/types/Navigation.ts'

    const emit = defineEmits<(e: 'navigate') => void>()

    // data
    const appStore = useAppStore()
    const route = useRoute()

    // computed
    const userHasTenants = computed(() => {
        return appStore.ifUserHasTenants()
    })

    const allowedPages = computed(() => {
        return appStore.getAllowedPages()
    })

    /**
     * An entry is visible when the user is allowed its page; a group is visible
     * when at least one of its children is. The tenants entry additionally
     * requires the user to actually have tenants.
     */
    const isVisible = (item: NavLink): boolean => {
        if (item.to === 'tenants-open-items' && !userHasTenants.value) return false
        if (item.items && item.items.length) return item.items.some(child => isVisible(child))
        if (item.page !== undefined) return allowedPages.value.includes(item.page)
        return true
    }

    /**
     * No `icon`: the sidebar has never shown per-item icons, and adding them
     * changed the whole left column's character. Metrics below match the old
     * menu.css -- 1rem labels, a right-rounded active row and a 4px accent bar
     * down its left edge.
     */
    const navUi = {
        link: 'py-[0.55rem] pr-2 pl-1 text-base font-semibold rounded-l-none before:rounded-l-none data-active:text-primary data-active:before:bg-primary/12 data-active:after:absolute data-active:after:left-0 data-active:after:inset-y-0 data-active:after:w-1 data-active:after:bg-primary',
        childLink: 'py-[0.5rem] text-base'
    }

    /**
     * A group is open when the current route is one of its children, and that
     * child is marked active. Without this nothing in the sidebar is
     * highlighted on any nested route -- six of them -- so the user loses any
     * sense of where they are.
     *
     * The open set has to be controlled: UNavigationMenu maps defaultOpen onto
     * the accordion's defaultValue, which is read once at mount, and this
     * sidebar never remounts across client-side navigation.
     */
    const containsRoute = (item: NavLink): boolean => {
        if (item.to && route.name === item.to) return true
        return (item.items ?? []).some(child => containsRoute(child))
    }

    const toMenuItem = (item: NavLink): Record<string, unknown> => ({
        label: item.label,
        value: item.label,
        active: item.to ? route.name === item.to : undefined,
        badge: item.number
            // solid orange disc, as before -- the default outlined square is
            // near-invisible against the sidebar
            ? { label: String(item.number), color: 'primary', variant: 'solid', ui: { base: 'rounded-full' } }
            : undefined,
        to: item.to ? { name: item.to } : undefined,
        children: item.items?.filter(isVisible).map(toMenuItem),
        onSelect: item.to ? () => emit('navigate') : undefined
    })

    const openGroups = ref<string[]>([])

    watch(() => route.name, () => {
        for (const item of navItems.value) {
            if (!item.items?.length || !containsRoute(item)) continue
            if (!openGroups.value.includes(item.label)) openGroups.value.push(item.label)
        }
    }, { immediate: true })

    const menuItems = computed(() => navItems.value.filter(isVisible).map(toMenuItem))
</script>
